import React, { useCallback } from 'react';

import { ErrorMessage } from '@eika/attention-message';
import { Button } from '@eika/button';
import { ErrorCard } from '@eika/card';
import { ErrorLabel } from '@eika/label';
import { Link, LinkType } from '@eika/link';
import { Loader } from '@eika/loader';

import { useConfig } from '../../../../defaultConfig';
import { useBankCountries } from '../../../../fetchHooks/useCountryDetails';
import { useValidateBank, useValidateIban } from '../../../../fetchHooks/useCrossBorderBankValidation';
import type { BankValidation } from '../../../../generated-types';
import useTranslation from '../../../../hooks/useTranslation';
import { CrossBorderPaymentActionType } from '../form/actions';
import { BankValidationOptions } from '../form/state';
import useFormContext from '../form/useFormContext';
import { receiverError } from '../form/validate';
import NationalIdInput from './NationalIdInput';

import './NewCrossBorderBankDetails.scss';

interface Props {
    validationErrors?: receiverError;
    setValidationErrors: (errors: receiverError) => void;
    setShowNextButton: (value: boolean) => void;
}

const NewCrossBorderBankDetails: React.FC<Props> = ({ validationErrors, setValidationErrors, setShowNextButton }) => {
    const translated = useTranslation();
    const { state, handleChange } = useFormContext();
    const { data: bankCountryList } = useBankCountries();
    const { contactPageLink } = useConfig();

    const onBankValidation = useCallback(
        (data: BankValidation) => {
            if (
                state.receiver?.accountType === 'BBAN' &&
                state.receiver?.bic?.value &&
                state.bankValidation?.bank?.bic?.value &&
                state.bankValidation.bank.bic.value.toLowerCase().includes(state.receiver.bic.value.toLowerCase())
            ) {
                return;
            }
            setShowNextButton(true);
            handleChange({
                type: CrossBorderPaymentActionType.SET_BANK_VALIDATION,
                payload: data,
            });
            if (state.receiver?.bankValidationOption === BankValidationOptions.BIC) {
                handleChange({
                    type: CrossBorderPaymentActionType.SET_RECEIVER_NATIONAL_ID,
                    payload: undefined,
                });
            }
            if (!data?.bank) setShowNextButton(false);
        },
        [
            handleChange,
            setShowNextButton,
            state.bankValidation?.bank?.bic?.value,
            state.receiver?.accountType,
            state.receiver?.bankValidationOption,
            state.receiver?.bic?.value,
        ],
    );
    const {
        data: ibanValidation,
        error: ibanValidationError,
        isLoading: loadingIbanValidation,
        refetch: retryIbanValidation,
    } = useValidateIban(
        state.receiver?.accountNumber ? { value: state.receiver.accountNumber } : undefined,
        state.receiver?.accountType === 'IBAN',
        onBankValidation,
    );

    const {
        data: bankValidation,
        error: bankValidationError,
        isLoading: loadingBankValidation,
        refetch: retryBankValidation,
    } = useValidateBank(
        state.receiver?.bic,
        state.receiver?.nationalId,
        state.country?.countryCode,
        state.receiver?.accountType === 'BBAN',
        onBankValidation,
    );

    const bankCountry = bankCountryList?.find(
        (country) => country.countryCode.value === state.bankValidation?.bank?.countryCode.value,
    );

    const bankInformation = ibanValidation ?? bankValidation ?? state.bankValidation;

    if (ibanValidationError || bankValidationError) {
        return (
            <ErrorCard
                className="select-cross-border-receiver__fetch-receivers-error"
                title={translated.errors.somethingWentWrong}
                description={translated.crossBorderPayment.newReceiver.errorIbanValidation}
            >
                <Button
                    size="small"
                    onClick={() => (ibanValidationError ? retryIbanValidation() : retryBankValidation())}
                >
                    {translated.tryAgain}
                </Button>
            </ErrorCard>
        );
    }

    if (loadingIbanValidation || loadingBankValidation) {
        return (
            <div className="bank-details-loader">
                <Loader className="new-cross-border-bank-details__loader" mini />
            </div>
        );
    }

    if (bankInformation?.valid === false) {
        return (
            <ErrorLabel
                className="new-cross-border-bank-details__invalid-iban"
                text={translated.crossBorderPayment.newReceiver.errorValidation}
            />
        );
    }

    if (bankInformation?.valid && !bankInformation?.bank) {
        return (
            <ErrorMessage
                className="new-cross-border-bank-details__no-bank-information"
                text={translated.crossBorderPayment.newReceiver.emptyBankValidation}
                clickableItem={
                    <Link href={contactPageLink?.url} linkType={LinkType.BUTTON_SMALL_NEGATIVE}>
                        {translated.crossBorderPayment.statusButton}
                    </Link>
                }
            />
        );
    }

    return (
        <>
            {bankInformation?.valid && (
                <dl className="new-cross-border-bank-details__description">
                    {ibanValidation && bankInformation.bank?.bic?.value && (
                        <>
                            <dt className="new-cross-border-bank-details__term">
                                {translated.crossBorderPayment.newReceiver.bicLabel}
                            </dt>
                            <dd className="new-cross-border-bank-details__term-description">
                                {bankInformation.bank?.bic?.value}
                            </dd>
                        </>
                    )}
                    <dt className="new-cross-border-bank-details__term">
                        {translated.crossBorderPayment.newReceiver.receiverBank}
                    </dt>
                    <dd className="new-cross-border-bank-details__bank-description">{bankInformation?.bank?.name}</dd>
                    <dd className="new-cross-border-bank-details__bank-description">
                        {bankInformation?.bank?.address}
                    </dd>
                    <dd className="new-cross-border-bank-details__bank-description">
                        {bankCountry?.name.toUpperCase()}
                    </dd>
                </dl>
            )}
            <>
                {state.country?.nationalIdRequirements?.required && state.receiver?.bic && !state.receiver.needBic && (
                    <NationalIdInput
                        value={state.receiver?.nationalId?.value ?? state.bankValidation?.bank?.nationalId?.value}
                        setValidationError={setValidationErrors}
                        validationError={validationErrors}
                    />
                )}
            </>
        </>
    );
};

export default NewCrossBorderBankDetails;
